<template>
  <BDropdown  v-model="isOpen"
              variant="outline-secondary"
              class="svd-control svd-height"
              menu-class="svd-menu scroll-y scroll-on-hover"
              toggle-class="d-flex justify-content-between align-items-center w-100 text-start"
              no-caret
              teleport
              strategy="fixed">
    <template #button-content>
      <span class="text-truncate me-2 svd-control-header-text font12">
        {{ modelValue ? modelValue.label : placeholder }}
      </span>
      <ChevronDown :size="14" class="opacity-50" />
    </template>

    <!-- Create New -->
    <BDropdownItem v-if="hasCreateButton" @click="handleCreate">
      <div class="d-flex align-items-center py-1 small">
        <Plus :size="16" class="me-2" />{{ $t('Webportal.ViewSelection.CreateNewView') }}
      </div>
    </BDropdownItem>

    <BDropdownDivider v-if="hasCreateButton" />

    <!-- Description -->
    <div  v-if="userDefinedViews && userDefinedViews.length"
          class="px-4 py-0 text-muted" style="font-size: 0.7rem; line-height: 1.3;">
      {{ $t('Webportal.ViewSelection.CustomReports')}}
    </div>

    <!-- Saved Views -->
    <BDropdownItem  v-for="view in userDefinedViews"
                    :key="view.id"
                    @click="handleSelect(view)"
                    :active="modelValue && modelValue.id === view.id"
                    class="view-item">
      <div class="d-flex align-items-center py-1">
        <Check  v-if="boardSetup && boardSetup.id === view.id"
                :size="14"
                :stroke-width="3"
                class="me-2 flex-shrink-0"
                style="color: var(--custom-accent-fixed);" />
        <span v-else style="width: 1rem;" class="me-2 flex-shrink-0"></span>
        <div class="d-flex flex-column flex-grow-1 min-w-0">
          <span class="font12 text-truncate fw-medium dropdown-width" :title="view.label">{{ view.label }}</span>
          <span class="font12 text-truncate text-muted dropdown-width">{{ view?.kpiCount }} KPI</span>
        </div>
        <div class="view-actions" @click.stop>
          <Trash2 :size="14" class="view-action-icon view-action-delete" @click="handleDelete(view)" />
        </div>
      </div>
    </BDropdownItem>

    <BDropdownDivider v-if="userDefinedViews && userDefinedViews.length" />

    <!-- Description -->
    <div class="px-4 py-0 text-muted" style="font-size: 0.7rem; line-height: 1.3;">
      {{ hasCreateButton ? $t('Webportal.ViewSelection.StandardReports') : $t('Webportal.ViewSelection.PredefinedSpecialReports') }}
    </div>

    <!-- Special Reports (Scrollable) -->
    <BDropdownItem  v-for="report in genericViews"
                    :key="report.id"
                    @click="handleSelect(report)"
                    :active="modelValue && modelValue.id === report.id">
      <div class="d-flex align-items-center py-1">
        <Check  v-if="boardSetup && boardSetup.id === report.id"
                :size="14"
                :stroke-width="3"
                class="me-2 flex-shrink-0"
                style="color: var(--custom-accent-fixed);" />
        <span v-else style="width: 1rem;" class="me-2 flex-shrink-0"></span>
        <div class="d-flex flex-column flex-grow-1 min-w-0">
          <span class="font12 text-truncate fw-medium dropdown-width" :title="report.label">{{ report.label }}</span>
          <span class="font12 text-truncate text-muted dropdown-width" v-if="report?.kpiCount">{{ report?.kpiCount }} KPI</span>
        </div>
      </div>
    </BDropdownItem>
  </BDropdown>
</template>

<script>
import { ChevronDown, Plus, Check, Pencil, Trash2 } from 'lucide-vue-next';

export default {
  name: 'SavedViewSelection',
  inject: {
    delegateCreateView: { default: null },
    delegateChangeView: { default: null },
    delegateDeleteView: { default: null }
  },
  components: { ChevronDown, Plus, Check, Pencil, Trash2 },
  props: {
    boardSetup: { type: Object, required: false, default: null},
    hasCreateButton: { type: Boolean, default: true },
    modelValue: { type: Object, default: null },
    placeholder: { type: String, default: 'standard' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false
    };
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.$nextTick(() => this.addScrollListeners());
      } else {
        this.removeScrollListeners();
      }
    }
  },
  beforeUnmount() {
    this.removeScrollListeners();
  },
  computed: {
    userDefinedViews() {
      return this.boardSetup?.views?.filter(x => !x.isGeneric) || [];
    },
    genericViews() {
      return this.boardSetup?.views?.filter(x => x.isGeneric) || [];
    }
  },
  methods: {
    handleSelect(item) {
      this.$emit('update:modelValue', item);
      if (this.delegateChangeView)
        this.delegateChangeView(item);
      else
        log.warn('No delegateChangeView provided for SavedViewSelection component');
    },

    handleCreate() {
      if (this.delegateCreateView)
        this.delegateCreateView(this.boardSetup);
      else
        log.warn('No delegateCreateView provided for SavedViewSelection component');
    },

    handleDelete(view) {
      if (this.delegateDeleteView)
        this.delegateDeleteView({ id: view.id });
      this.isOpen = false;
    },

    onOutsideScroll(event) {
      const dropdownMenu = this.$el?.querySelector('.svd-menu');
      if (dropdownMenu && !dropdownMenu.contains(event.target)) {
        this.isOpen = false;
      }
    },

    addScrollListeners() {
      this._scrollHandler = this.onOutsideScroll.bind(this);
      window.addEventListener('scroll', this._scrollHandler, true);
    },

    removeScrollListeners() {
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler, true);
        this._scrollHandler = null;
      }
    }
  }
}
</script>

<style lang="scss">
.dropdown-fix-right {
  right: 0 !important;
  left: auto !important;
}
</style>

<style lang="scss" scoped>
.svd-height {
  height: 2.25rem;
  :deep(.btn) {
    height: 2.25rem;
  }
}

.svd-control {
  width: 15rem;
  background: var(--custom-black-05);
}

.svd-control-header-text {
  color: var(--custom-black-90);
  align-content: center;
  max-width: calc(100% - 1rem);
  height: 1.5rem;
  [data-bs-theme="dark"] & {
    color: var(--custom-white-90);
  }
}
:deep(.svd-menu) {
  width: 15rem;
  max-height: 50vh;
  overflow-x: hidden;
}
:deep(.svd-menu .dropdown-item) {
  white-space: normal;
  overflow: hidden;
  max-width: 100%;
}
.font-muted {
  color: var(--custom-black-50);
  [data-bs-theme="dark"] & {
    color: var(--color-muted-number);
  }
}

.header {
  color: var(--color-foreground);
}

.dropdown-item:hover {
  max-height: 300px;
}

.dropdown-width{
  max-width: 12rem;
}

.view-actions {
  display: none;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  flex-shrink: 0;
}

.view-item:hover .view-actions {
  display: flex;
}

.view-item:hover .dropdown-width {
  max-width: 8rem;
}

.view-action-icon {
  color: var(--bs-secondary-color);
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: color 0.15s;

  &:hover {
    color: var(--color-foreground);
  }
}

.view-action-delete:hover {
  color: var(--bs-danger);
}
</style>
